import type { AiSuggestionInteractionPoint } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Selection } from '@atlaskit/editor-prosemirror/state';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { getSuggestionChangeStart } from '../pm-plugins/utils/suggestionToolCallRange';
import { triggerSuggestedRemix } from '../ui/components/SuggestionCard/triggerSuggestedRemix';
import { addAcceptSuggestionAnalytics } from '../ui/utils/analytics';
import { updateSuggestionStatus } from '../ui/utils/api';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';
import type { AcceptSuggestionAction } from './types';

export const acceptSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => AcceptSuggestionAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(
		id: string,
		interactionPoint: AiSuggestionInteractionPoint = 'sidebar',
		isDiffHidden: boolean = false,
	): boolean => {
		const suggestion = api?.aiSuggestions?.sharedState.currentState()?.suggestionById.get(id);
		if (!suggestion) {
			return false;
		}

		const isRemix = suggestion.actionKind === 'SUGGESTED_REMIX';

		api?.core?.actions.execute(({ tr }) => {
			addAcceptSuggestionAnalytics(api?.analytics?.actions)(
				tr,
				suggestion,
				interactionPoint,
				isDiffHidden,
			);
			tr.setMeta('addToHistory', false);
			// Move the selection to the change before the accept so history bookmarks it: undo then
			// restores and scrolls to it, instead of the pre-accept selection (often the doc start).
			const changeStart = isRemix ? undefined : getSuggestionChangeStart(tr.doc, suggestion);
			if (changeStart !== undefined && changeStart <= tr.doc.content.size) {
				tr.setSelection(Selection.near(tr.doc.resolve(changeStart)));
			}
			return tr;
		});

		api?.core?.actions.execute(({ tr }) => {
			api?.showDiff?.commands.hideDiff?.({ tr });

			if (isRemix) {
				tr.setMeta(aiSuggestionsPluginKey, { action: 'REMOVE_SUGGESTION', id });
			} else {
				applyToolCalls({ tr, suggestion });
				tr.setMeta('scrollIntoView', false);
				tr.setMeta(aiSuggestionsPluginKey, { action: 'ACCEPT_SUGGESTION', suggestion });
			}

			return tr;
		});

		updateSuggestionStatus({ updates: [{ id, status: 'ACTIONED' }] });

		if (isRemix) {
			const action = suggestion.action;
			triggerSuggestedRemix({
				api,
				launchOptions: action?.launchOptions,
				localIds: action?.localIds,
				userPrompt: action?.userPrompt,
			});
		}

		return true;
	};
